/**
 * The front door.
 *
 * Not a card floating in the middle of an empty page: the name of the machine, set large, and a
 * plain statement of what it does, with the two fields beside it across a rule. It is the only
 * screen a new developer sees before they are let in, so it should say what they are signing
 * into.
 *
 * Two fields, and the server's own wording for whatever went wrong -- it deliberately gives the
 * same message for an unknown address and a wrong password, so this page must not elaborate.
 */
import { useState } from 'react';
import { ApiError, api, type Session } from '../api';

export function SignIn({ onSignedIn }: { onSignedIn: (session: Session) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.login(email, password);
      onSignedIn(await api.me());
    } catch (failure) {
      setError((failure as ApiError).message);
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="signin-page">
      <div className="signin-said">
        <p className="signin-mark">whitelabel<span>builds</span></p>
        <p className="signin-lede">Signed Android releases for the radio apps.</p>
        <p className="signin-body">Android and iOS releases, signed and ready to upload.</p>
      </div>

      <form className="signin" onSubmit={submit}>
        {error && <p className="signin-error" role="alert">{error}</p>}

        <div className="field">
          <label htmlFor="signin-email">Email</label>
          <input
            id="signin-email" name="email" type="email" autoComplete="username"
            required autoFocus value={email} onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="signin-password">Password</label>
          <input
            id="signin-password" name="password" type="password" autoComplete="current-password"
            required value={password} onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        <button className="btn" type="submit" disabled={busy}>
          {busy ? 'Signing in' : 'Sign in'}
        </button>

        <p className="signin-foot">Accounts are created by an admin.</p>
      </form>
    </div>
  );
}

/** Shown in place of everything else when an admin set the password and it is still theirs. */
export function ChoosePassword({ minLength, onDone }: { minLength: number; onDone: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (next !== confirm) {
      setError('The two new passwords do not match.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api.changePassword(current, next);
      onDone();
    } catch (failure) {
      setError((failure as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="signin-page">
      <div className="signin-said">
        <p className="signin-mark">whitelabel<span>builds</span></p>
        <p className="signin-lede">Choose your own password.</p>
        <p className="signin-body">At least {minLength} characters.</p>
      </div>

      <form className="signin" onSubmit={submit}>
        {error && <p className="signin-error" role="alert">{error}</p>}

        <div className="field">
          <label htmlFor="cp-current">Current password</label>
          <input id="cp-current" type="password" autoComplete="current-password" required
                 value={current} onChange={(event) => setCurrent(event.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="cp-new">New password</label>
          <input id="cp-new" type="password" autoComplete="new-password" required
                 value={next} onChange={(event) => setNext(event.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="cp-confirm">New password again</label>
          <input id="cp-confirm" type="password" autoComplete="new-password" required
                 value={confirm} onChange={(event) => setConfirm(event.target.value)} />
        </div>

        <button className="btn" type="submit" disabled={busy}>
          {busy ? 'Saving' : 'Save and sign in again'}
        </button>
        <p className="signin-foot">This ends every session, including this one.</p>
      </form>
    </div>
  );
}
